import React, { useRef, useLayoutEffect, useEffect } from 'react';
import { Spin, Modal, Button, Select, Tooltip, Dropdown } from 'antd';
import { message as antdMessage } from 'antd';
import modalRegistry from '../../modals';
import SelectDCCFileModal from '../../modals/SelectDCCFileModal';
import dayjs from 'dayjs';
import { motion, AnimatePresence } from 'framer-motion';
import logger from '../../../utils/logger';
import { fetchSqlDataWithQuery } from '../../../services/api';
import { resolveFieldTextAlign } from '../PdfFormCreationFiles/constants';
import FormattedNumberInput from '../PdfFormCreationFiles/FormattedNumberInput';
import { formatNumberDisplay } from '../PdfFormCreationFiles/numberFieldFormat';
import PrintButton from '../PrintButton';

export const FORM_PRINT_CSS = `
@page { size: auto; margin: 8mm; }
html, body { margin: 0 !important; background: #fff !important; }
.form-print-root {
  overflow: visible !important;
  background: #fff !important;
  box-shadow: none !important;
}
.form-print-root svg {
  max-width: none !important;
  display: block;
  -webkit-print-color-adjust: exact !important;
  print-color-adjust: exact !important;
}
.form-print-root svg * {
  -webkit-print-color-adjust: exact !important;
  print-color-adjust: exact !important;
}
/* Screen-only edit tints (inline backgrounds on overlays/Ant controls) must not print. */
.form-print-root .form-print-fields,
.form-print-root .form-print-fields *,
.form-print-root input,
.form-print-root textarea,
.form-print-root select,
.form-print-root button,
.form-print-root .ant-input,
.form-print-root .ant-input-affix-wrapper,
.form-print-root .ant-select,
.form-print-root .ant-select-selector,
.form-print-root .ant-select-selection-item,
.form-print-root .ant-picker,
.form-print-root .ant-picker-input > input {
  color: #000 !important;
  background: transparent !important;
  background-color: transparent !important;
  background-image: none !important;
  box-shadow: none !important;
  -webkit-box-shadow: none !important;
  border: none !important;
  border-color: transparent !important;
  outline: none !important;
  -webkit-print-color-adjust: economy !important;
  print-color-adjust: economy !important;
}
.ant-spin-spinning,
.ant-modal-root,
.ant-select-dropdown { display: none !important; }
`;

// Type for a single field definition (generic)
export type GenericFieldDefinition = {
  name: string;
  label: string;
  type: string;
  required?: boolean;
  modalComponent?: string;
  options?: any[];
  position?: {
    x0?: number; y0?: number; x1?: number; y1?: number;
  };
  fontsize?: number;
  textAlign?: 'left' | 'center' | 'right';
  defaultValue?: any;
};

// Props for the generic form fill component
export type GenericFormFillProps = {
  svg: string; // SVG string background
  fields: GenericFieldDefinition[]; // overlay fields
  formSpecificFields: Record<string, any>; // e.g. { form_ref, form_record_id, ... }
  formDefinition?: any; // Accepts full form definition from caller
  onSave?: (allValues: Record<string, any>) => void;
  loading?: boolean;
  fieldValues: Record<string, any>;
  setFieldValues: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  validationErrors?: Record<string, boolean>;
  colorless?: boolean; // when true, render with no highlight background/borders (view mode)
  hideOverlaysInView?: boolean; // when true and colorless, do not render overlays at all
  // Optional predicate to mark individual fields read-only (renders like colorless per field)
  isFieldReadOnly?: (field: GenericFieldDefinition) => boolean;
  showPrintButton?: boolean;
  printRootRef?: React.RefObject<HTMLDivElement>;
};

function getString(val: unknown, fallback = ''): string {
  return typeof val === 'string' ? val : fallback;
}

function normalizeOption(o: any): { value: string; label: string } {
  if (typeof o === 'string') return { value: o, label: o };
  const value = String(o?.value ?? o?.label ?? '');
  const label = String(o?.label ?? o?.value ?? value);
  return { value, label };
}

// Utility to dynamically shrink font size to fit field width
function useAutoFontSize(text: string, width: number, initialFontSize: number, fontFamily?: string, minFontSize = 8) {
  const [fontSize, setFontSize] = React.useState(initialFontSize);
  const spanRef = React.useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    if (!spanRef.current) return;
    let size = initialFontSize;
    spanRef.current.style.fontSize = size + 'px';
    spanRef.current.style.fontFamily = fontFamily || 'inherit';
    let spanWidth = spanRef.current.offsetWidth;
    while (spanWidth > width - 8 && size > minFontSize) {
      size -= 1;
      spanRef.current.style.fontSize = size + 'px';
      spanWidth = spanRef.current.offsetWidth;
    }
    setFontSize(size);
  }, [text, width, initialFontSize, fontFamily, minFontSize]);
  return { fontSize, spanRef };
}

// Overlay input with auto font size and magnify on hover/focus
const OverlayInput: React.FC<{
  field: GenericFieldDefinition,
  value: string,
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
  baseStyle: React.CSSProperties,
}> = ({ field, value, onChange, baseStyle }) => {
  const { x0 = 0, x1 = 0 } = field.position || {};
  const { fontSize, spanRef } = useAutoFontSize(value, (x1 - x0) || 120, field.fontsize || 12, undefined, 8);
  return (
    <motion.input
      name={field.name}
      type={field.type || 'text'}
      required={field.required}
      value={value}
      onChange={onChange}
      style={{
        ...baseStyle,
        fontSize,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        background: value ? undefined : baseStyle.background || '#e6f7ff', // Remove background if filled
      }}
      autoComplete="off"
      placeholder=""
      ref={spanRef as any}
      whileHover={{ scaleX: 1.2, scaleY: 1.3 }}
      whileFocus={{ scaleX: 1.2, scaleY: 1.3 }}
      transition={{ duration: 0.2 }}
    />
  );
};

// Signature text display with auto font size fitting
const SignatureDisplay: React.FC<{ text: string; width: number; initialFontSize: number; style?: React.CSSProperties; debugLabel?: string }>
  = ({ text, width, initialFontSize, style, debugLabel }) => {
  // Subtract a small amount to account for container padding/borders
  const available = Math.max(10, (width || 120) - 4);
  const { fontSize, spanRef } = useAutoFontSize(text, available, initialFontSize || 12, undefined, 8);
  return (
    <span
      ref={spanRef as any}
      style={{
        fontSize,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: 'inline-block',
        maxWidth: available,
        ...style,
      }}
    >
      {text}
    </span>
  );
};

// Multiline version for signature: renders each line fitted separately
const SignatureMultiline: React.FC<{ text: string; width: number; initialFontSize: number }>
  = ({ text, width, initialFontSize }) => {
  const lines = (text || '').split(/\r?\n/);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.1, width: '100%' }}>
      {lines.map((ln, i) => (
        <SignatureDisplay key={i} text={ln} width={width} initialFontSize={initialFontSize} />
      ))}
    </div>
  );
};

// Overlay select/modal with auto font size
const OverlaySelectModal: React.FC<{
  field: GenericFieldDefinition,
  value: string,
  onClick: (e: React.MouseEvent) => void,
  baseStyle: React.CSSProperties,
}> = ({ field, value, onClick, baseStyle }) => {
  const { x0 = 0, x1 = 0 } = field.position || {};
  const { fontSize, spanRef } = useAutoFontSize(value, (x1 - x0) || 120, field.fontsize || 12, undefined, 8);
  // Only remove background if not placeholder
  const isFilled = value && value !== `select ${field.label}`;
  return (
    <div
      style={{
        ...baseStyle,
        fontSize,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        background: isFilled ? undefined : baseStyle.background || '#e6f7ff',
      }}
      onClick={onClick}
    >
      <span ref={spanRef} style={{ display: 'inline-block', width: '100%' }}>{value}</span>
    </div>
  );
};

const parseAuthorizedSignatories = (raw: any): string[] => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String).map((s) => s.trim()).filter(Boolean);

  const s = String(raw).trim();
  if (!s) return [];

  try {
    let parsed: any = JSON.parse(s);
    if (typeof parsed === 'string') {
      parsed = JSON.parse(parsed);
    }
    if (Array.isArray(parsed)) {
      return parsed.map(String).map((x) => x.trim()).filter(Boolean);
    }
  } catch {}

  return s.split(/[;,:\s]+/).map((x) => x.trim()).filter(Boolean);
};

const GenericFormFill: React.FC<GenericFormFillProps> = ({
  svg,
  fields,
  formSpecificFields,
  formDefinition,
  onSave,
  loading = false,
  fieldValues,
  setFieldValues,
  validationErrors,
  colorless = false,
  hideOverlaysInView = false,
  isFieldReadOnly,
  showPrintButton = true,
  printRootRef,
}) => {
  const [modalField, setModalField] = React.useState<GenericFieldDefinition | null>(null);
  const [modalVisible, setModalVisible] = React.useState(false);
  const [modalPosition, setModalPosition] = React.useState<{ top: number; left: number } | null>(null);
  const [selectedOption, setSelectedOption] = React.useState<any>(null);
  // Inline dropdown (field.type === 'dropdown') open state: only one open at a time
  const [inlineDropdownOpenField, setInlineDropdownOpenField] = React.useState<string | null>(null);
  // signature pad not used currently; removed refs

  useEffect(() => {
    if (modalVisible) setSelectedOption(fieldValues[modalField?.name!] ?? null);
  }, [modalVisible, modalField, fieldValues]);

  // Auto-populate 'submitted_by' fields once if empty
  useEffect(() => {
    const fillSubmittedBy = async () => {
      try {
        const updates: Record<string, any> = {};
        for (const f of fields || []) {
          if (f?.type === 'submitted_by') {
            const current = fieldValues?.[f.name];
            if (!current || (typeof current === 'string' && current.trim() === '')) {
              const myId = localStorage.getItem('personnel_id') || '';
              let fullName = '';
              if (myId) {
                try {
                  const sql = `SELECT first_name, last_name FROM adm_personnel WHERE pers_id = '${myId}' LIMIT 1`;
                  const rows = await fetchSqlDataWithQuery(sql);
                  const first = rows?.[0]?.first_name || '';
                  const last = rows?.[0]?.last_name || '';
                  fullName = `${first} ${last}`.trim() || myId;
                } catch {
                  fullName = myId;
                }
              }
              const today = dayjs().format('YYYY-MM-DD');
              const mode = f && f.defaultValue === 'single_line' ? 'single_line' : 'two_line';
              const display = mode === 'single_line'
                ? `${fullName} — ${today}`.trim()
                : `${fullName}\n${today}`.trim();
              updates[f.name] = display;
            }
          }
        }
        if (Object.keys(updates).length > 0) {
          setFieldValues(prev => ({ ...prev, ...updates }));
        }
      } catch {}
    };
    fillSubmittedBy();
    // Only when fields change or fieldValues reference changes
  }, [fields, setFieldValues]);

  // --- Responsive scaling logic ---
  const localContainerRef = React.useRef<HTMLDivElement>(null);
  const containerRef = (printRootRef ?? localContainerRef) as React.RefObject<HTMLDivElement>;
  const [scale, setScale] = React.useState(1);

  // Extract base dimensions:
  // - prefer canonical units saved with the definition (supports both legacy top-level pdfWidth/pdfHeight and nested form_json.pdfWidth/pdfHeight)
  // - fallback to SVG viewBox if not present
  const defAny: any = formDefinition as any;
  let defJson: any = defAny?.form_json;
  if (typeof defJson === 'string') {
    try { defJson = JSON.parse(defJson); } catch { /* ignore */ }
  }
  const canonicalW =
    (defAny && typeof defAny.pdfWidth === 'number' ? defAny.pdfWidth : undefined) ??
    (defJson && typeof defJson.pdfWidth === 'number' ? defJson.pdfWidth : undefined);
  const canonicalH =
    (defAny && typeof defAny.pdfHeight === 'number' ? defAny.pdfHeight : undefined) ??
    (defJson && typeof defJson.pdfHeight === 'number' ? defJson.pdfHeight : undefined);

  let svgWidth = typeof canonicalW === 'number' ? canonicalW : 1000;
  let svgHeight = typeof canonicalH === 'number' ? canonicalH : 1400;

  if ((!canonicalW || !canonicalH) && svg) {
    const match = svg.match(/viewBox="([^"]+)"/);
    if (match) {
      const [, viewBox] = match;
      const parts = viewBox.split(' ').map(Number);
      if (parts.length === 4) {
        svgWidth = parts[2];
        svgHeight = parts[3];
      }
    }
  }

  // Perf: log when input props that affect rendering change
  useEffect(() => {
    logger.debug(`[GenericFormFill] props change: svgChars=${svg ? svg.length : 0}, fields=${Array.isArray(fields) ? fields.length : 0}`);
  }, [svg, fields]);

  useEffect(() => {
    const updateScale = () => {
      if (containerRef.current) {
        const availableWidth = containerRef.current.offsetWidth;
        // Only scale down to fit, never scale up
        const newScale = Math.min(1, availableWidth / svgWidth);
        setScale(newScale);
      }
    };
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [svgWidth]);

  // Input handlers
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFieldValues(prev => ({ ...prev, [name]: value }));
  };
  const handleDateChange = (name: string, dateString: string) => {
    setFieldValues(prev => ({ ...prev, [name]: dateString }));
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFieldValues(prev => ({ ...prev, [name]: checked }));
  };

  // Modal logic
  const handleOpenModal = (field: GenericFieldDefinition, event: React.MouseEvent) => {
    setModalField(field);
    setModalVisible(true);
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    setModalPosition({
      top: rect.bottom + window.scrollY + 8, // 8px offset below
      left: rect.left + window.scrollX,
    });
  };
  const handleModalOk = (val: any) => {
    if (modalField) {
      const stored = Array.isArray(val)
        ? val.map((item) => String(item ?? '').trim()).filter(Boolean).join('\n')
        : (val ?? '');
      setFieldValues(prev => ({ ...prev, [modalField.name]: stored }));
    }
    setModalVisible(false);
    setModalField(null);
  };
  const handleModalCancel = () => {
    setModalVisible(false);
    setModalField(null);
  };

  // Overlay field renderer (use the fields prop directly, no merging)
  const renderOverlayField = (field: GenericFieldDefinition) => {
    const { x0 = 0, y0 = 0, x1 = 0, y1 = 0 } = field.position || {};
    const height = (y1 - y0) || 32;
    const isRequired = !!field.required;
    const isInvalid = validationErrors?.[field.name];
    const value = fieldValues[field.name];
    const readOnly = !!(isFieldReadOnly && isFieldReadOnly(field));
    const viewMode = colorless || readOnly;
    const fontSize = typeof field.fontsize === 'number' ? field.fontsize : 12;
    const textAlign = resolveFieldTextAlign(field.textAlign);
    const isEmptyValue = value === undefined || value === null || (typeof value === 'string' && value.trim() === '');
    const backgroundColor = viewMode ? 'transparent' : (isRequired ? '#fff0f0' : '#e6f7ff');
    const borderStyle = viewMode ? 'none' : (isInvalid ? '2px solid #ff4d4f' : (isRequired ? '1px solid #d9d9d9' : 'none'));
    const baseStyle = {
      position: 'absolute' as const,
      left: x0 * scale,
      top: y0 * scale,
      width: ((x1 - x0) || 120) * scale,
      height: height * scale,
      zIndex: 2,
      background: backgroundColor,
      color: viewMode ? '#222' : (field.modalComponent ? '#1677ff' : '#222'),
      textAlign,
      fontSize,
      padding: 2,
      border: borderStyle,
      borderRadius: 4,
    };

    // Dropdown (runtime): invisible control; show value in the field span; open a dropdown menu on click
    if (field.type === 'dropdown') {
      const current = getString(fieldValues[field.name], '');
      const opts = (Array.isArray(field.options) ? field.options : []).map(normalizeOption).filter(o => !!o.value);
      const label = (opts.find(o => o.value === current)?.label || current || '').toString();
      const fontSize = typeof field.fontsize === 'number' ? field.fontsize : 12;
      const placeholderText = `select ${field.label}`;
      const titleText = label || placeholderText;

      if (viewMode) {
        const display = label || '';
        return (
          <div
            title={display}
            style={{ ...baseStyle, fontSize, display: 'flex', alignItems: 'center', pointerEvents: 'none', mixBlendMode: 'multiply' }}
          >
            <span style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign }}>{display}</span>
          </div>
        );
      }

      const items = opts.map(o => ({
        key: o.value,
        label: <span style={{ fontSize }}>{o.label}</span>,
      }));

      return (
        <Dropdown
          trigger={['click']}
          placement="bottomLeft"
          open={inlineDropdownOpenField === field.name}
          onOpenChange={(next) => setInlineDropdownOpenField(next ? field.name : null)}
          menu={{
            items,
            onClick: ({ key }) => {
              setFieldValues(prev => ({ ...prev, [field.name]: String(key) }));
              setInlineDropdownOpenField(null);
            },
          }}
          overlayStyle={{ fontSize, zIndex: 3000 }}
          getPopupContainer={(triggerNode) => (containerRef.current ?? triggerNode.parentElement ?? document.body) as HTMLElement}
        >
          <button
            tabIndex={0}
            type="button"
            title={titleText}
            name={field.name}
            style={{
              ...baseStyle,
              fontSize,
              cursor: 'pointer',
              zIndex: 100,
              pointerEvents: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            <span style={{ fontWeight: 600, color: baseStyle.color, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', width: '100%', textAlign }}>
              {label ? label : <span className="no-print">{placeholderText}</span>}
            </span>
          </button>
        </Dropdown>
      );
    }

    // Inline Select (runtime) when no modal or InlineModal is chosen
    if (field.type === 'select' && (!field.modalComponent || field.modalComponent === 'InlineModal')) {
      const text = getString(fieldValues[field.name], '');
      const opts = Array.isArray(field.options) ? field.options : [];
      const fontSize = typeof field.fontsize === 'number' ? field.fontsize : 12;
      if (viewMode) {
        return (
          <div
            title={text}
            style={{ ...baseStyle, fontSize, display: 'flex', alignItems: 'center', pointerEvents: 'none', mixBlendMode: 'multiply' }}
          >
            <span style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign }}>{text}</span>
          </div>
        );
      }
      return (
        <Select
          className="wp-overlay-inline-select"
          style={{
            ...baseStyle,
            fontSize,
            width: baseStyle.width as number,
            height: baseStyle.height as number,
            ['--wp-overlay-font-size' as any]: `${fontSize}px`,
          }}
          value={text || undefined}
          placeholder={field.label}
          options={opts.map(normalizeOption)}
          onChange={(val) => setFieldValues(prev => ({ ...prev, [field.name]: val }))}
          dropdownMatchSelectWidth
          popupClassName="wp-overlay-inline-select-popup"
          dropdownStyle={{ ['--wp-overlay-font-size' as any]: `${fontSize}px` } as React.CSSProperties}
          size="small"
        />
      );
    }

    // Inline Radio (runtime) horizontal
    if (field.type === 'radio') {
      const current = getString(fieldValues[field.name], '');
      const opts = (Array.isArray(field.options) ? field.options : [])
        .map(normalizeOption)
        .filter(o => !!o.value);
      const fontSize = typeof field.fontsize === 'number' ? field.fontsize : 12;
      const radioScale = Math.max(0.8, Math.min(2, fontSize / 12));
      if (viewMode) {
        const label = opts.find(o => o.value === current)?.label || current;
        return (
          <div
            title={label}
            style={{ ...baseStyle, fontSize, display: 'flex', alignItems: 'center', pointerEvents: 'none', mixBlendMode: 'multiply' }}
          >
            <span style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign }}>{label}</span>
          </div>
        );
      }

      return (
        <div
          style={{
            ...baseStyle,
            fontSize,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-evenly',
            gap: 0,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            padding: 0,
          }}
        >
          {opts.map((o) => {
            const checked = current === o.value;
            return (
              <label
                key={o.value}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  padding: 1,
                  flex: '1 1 0',
                  maxWidth: '50%',
                  overflow: 'hidden',
                }}
              >
                <input
                  type="radio"
                  name={field.name}
                  checked={checked}
                  style={{ margin: 0, padding: 0, transform: `scale(${radioScale})`, transformOrigin: 'center' }}
                  onChange={() => setFieldValues(prev => ({ ...prev, [field.name]: o.value }))}
                />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.label || o.value}</span>
              </label>
            );
          })}
        </div>
      );
    }

    // Signature field: authorization check against predefined personnel_id list
    if (field.type === 'signature') {
      const rawAllowed = field.defaultValue ?? '';
      const allowed = parseAuthorizedSignatories(rawAllowed);
      const myId = localStorage.getItem('personnel_id') || '';
      const isAllowed = !!myId && allowed.includes(String(myId));

      const tooltipTitle = (
        <div style={{ maxWidth: 320 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Signature authorization</div>
          <div style={{ marginBottom: 6 }}>
            {myId
              ? (isAllowed ? 'You are authorized to sign this field.' : 'You are NOT authorized to sign this field.')
              : 'No personnel_id found for current user.'}
          </div>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Authorized signatories</div>
          {allowed.length ? (
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{allowed.join(', ')}</div>
          ) : (
            <div>None configured</div>
          )}
        </div>
      );

      const onClick = () => {
        try {
          const raw = field.defaultValue ?? '';
          const allowed: string[] = parseAuthorizedSignatories(raw);
          const myId = localStorage.getItem('personnel_id') || '';
          if (!Array.isArray(allowed)) throw new Error('Invalid signatory list');
          if (!myId) {
            antdMessage.warning('No personnel_id found for current user. Please re-login.');
            return;
          }
          const isAllowed = allowed.includes(String(myId));
          if (isAllowed) {
            // Authorized: show confirmation modal. If already has a value, allow revoke.
            const currentVal = getString(fieldValues[field.name], '');
            if (currentVal) {
              Modal.confirm({
                title: 'Signature already confirmed',
                content: (
                  <div>
                    <div>Current: {currentVal}</div>
                    <div style={{ marginTop: 8 }}>Do you want to revoke this confirmation?</div>
                  </div>
                ),
                okText: 'Revoke',
                cancelText: 'Close',
                onOk: () => {
                  setFieldValues(prev => ({ ...prev, [field.name]: '' }));
                  antdMessage.success('Confirmation revoked.');
                },
              });
              return;
            }

            Modal.confirm({
              title: 'Confirm Signature',
              content: 'You are authorized to sign this field. Do you confirm?',
              okText: 'Confirm',
              cancelText: 'Cancel',
              onOk: async () => {
                try {
                  const sql = `SELECT first_name, last_name FROM adm_personnel WHERE pers_id = '${myId}' LIMIT 1`;
                  const rows = await fetchSqlDataWithQuery(sql);
                  const first = rows?.[0]?.first_name || '';
                  const last = rows?.[0]?.last_name || '';
                  const fullName = `${first} ${last}`.trim() || myId;
                  const today = dayjs().format('YYYY-MM-DD');
                  const display = `${fullName}\n${today}`;
                  setFieldValues(prev => ({ ...prev, [field.name]: display }));
                  antdMessage.success('Signature confirmed.');
                } catch (e) {
                  antdMessage.error('Failed to fetch user info for signature.');
                }
              },
            });
          } else {
            antdMessage.error('You are not authorized to sign this field.');
          }
        } catch (e) {
          antdMessage.error('Could not read authorized signatories for this field.');
        }
      };
      const label = (field.label || field.name || 'Signature').toString();
      const valueText = getString(fieldValues[field.name], '') || 'Confirm';
      const fieldWidth = ((x1 - x0) || 120) * scale;
      if (viewMode) {
        const display = getString(fieldValues[field.name], '');
        return (
          <div
            title={label}
            style={{
              ...baseStyle,
              background: 'transparent',
              border: 'none',
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#000',
            }}
          >
            {display.includes('\n')
              ? <SignatureMultiline text={display} width={fieldWidth} initialFontSize={field.fontsize || 12} />
              : <SignatureDisplay text={display} width={fieldWidth} initialFontSize={field.fontsize || 12} debugLabel={field.name} />}
          </div>
        );
      }
      return (
        <Tooltip title={tooltipTitle} placement="top">
          <button
            type="button"
            onClick={onClick}
            style={{
              ...baseStyle,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: viewMode ? 'default' : 'pointer',
            }}
          >
            {valueText.includes('\n')
              ? <SignatureMultiline text={valueText} width={fieldWidth} initialFontSize={field.fontsize || 12} />
              : <SignatureDisplay text={valueText} width={fieldWidth} initialFontSize={field.fontsize || 12} debugLabel={field.name} />}
          </button>
        </Tooltip>
      );
    }

    // Submitted By: read-only, with two_line or single_line layout
    if (field.type === 'submitted_by') {
      const valueText = getString(fieldValues[field.name], '');
      const mode = field && field.defaultValue === 'single_line' ? 'single_line' : 'two_line';
      const fieldWidth = ((x1 - x0) || 120) * scale;
      if (viewMode) {
        const display = valueText;
        return (
          <div
            title={display.replace(/\n/g, ' ')}
            style={{
              ...baseStyle,
              background: 'transparent',
              border: 'none',
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#000',
            }}
          >
            {mode === 'two_line' || display.includes('\n')
              ? <SignatureMultiline text={display} width={fieldWidth} initialFontSize={field.fontsize || 12} />
              : <SignatureDisplay text={display} width={fieldWidth} initialFontSize={field.fontsize || 12} />}
          </div>
        );
      }
      return (
        <div
          title={valueText.replace(/\n/g, ' ')}
          style={{
            ...baseStyle,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          {mode === 'two_line' || valueText.includes('\n')
            ? <SignatureMultiline text={valueText} width={fieldWidth} initialFontSize={field.fontsize || 12} />
            : <SignatureDisplay text={valueText} width={fieldWidth} initialFontSize={field.fontsize || 12} />}
        </div>
      );
    }

    // If field has a modalComponent, use the associated modal from modalRegistry
    if (field.modalComponent) {
      if (viewMode && isEmptyValue) {
        // In view mode, keep overlays fully transparent
        return (
          <div
            style={{
              ...baseStyle,
              background: 'transparent',
              border: 'none',
              pointerEvents: 'none',
            }}
          />
        );
      }
      const storedValue = fieldValues[field.name] ? String(fieldValues[field.name]) : '';
      const storedPaths = storedValue.split(/\r?\n/).map((item) => item.trim()).filter(Boolean);
      const displayValue = storedPaths.length > 1
        ? storedPaths.map((item) => item.split('/').pop() || item).join(', ')
        : storedValue;
      const placeholderText = `select ${field.label}`;
      const value = displayValue || (viewMode ? '' : placeholderText);
      if (viewMode) {
        return (
          <div
            title={storedValue || value}
            style={{
              ...baseStyle,
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              mixBlendMode: 'multiply',
            }}
          >
            <span style={{ fontWeight: 400, color: baseStyle.color, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', width: '100%', textAlign }}>{value}</span>
          </div>
        );
      }
      return (
        <button
          tabIndex={0}
          type="button"
          title={storedValue || value}
          name={field.name}
          style={{
            ...baseStyle,
            border: baseStyle.border,
            background: baseStyle.background,
            color: baseStyle.color,
            cursor: 'pointer',
            zIndex: 100,
            pointerEvents: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
          }}
          onClick={e => handleOpenModal(field, e)}
        >
          <span style={{ fontWeight: 600, color: baseStyle.color, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', width: '100%', textAlign }}>
            {displayValue ? displayValue : <span className="no-print">{placeholderText}</span>}
          </span>
        </button>
      );
    }

    // All other fields: input, checkbox, etc.
    if (field.type === 'checkbox') {
      if (viewMode) {
        const checked = !!fieldValues[field.name];
        // Non-interactive, transparent representation to avoid masking SVG lines
        return (
          <div
            aria-label={checked ? 'checked' : 'unchecked'}
            style={{
              ...baseStyle,
              width: 18, height: 18, marginTop: 6,
              background: 'transparent',
              border: 'none',
              pointerEvents: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#000',
              mixBlendMode: 'multiply',
              fontSize,
              lineHeight: 1,
            }}
          >
            {checked ? '✓' : ''}
          </div>
        );
      }
      return (
        <div
          style={{
            ...baseStyle,
            pointerEvents: 'auto',
            background: 'rgba(230,242,255,0.2)',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid #91caff',
            padding: 0,
            margin: 0
          }}
        >
          <input
            name={field.name}
            type="checkbox"
            checked={!!fieldValues[field.name]}
            onChange={handleCheckboxChange}
            style={{
              width: 18,
              height: 18,
              margin: 0,
              padding: 0,
              border: 'none',
              background: 'transparent',
              outline: 'none'
            }}
          />
        </div>
      );
    }
    // Date fields: keep AntD or browser default placeholder
    if (field.type === 'date') {
      if (viewMode && isEmptyValue) {
        // In view mode, keep overlays fully transparent
        return (
          <div
            style={{
              ...baseStyle,
              background: 'transparent',
              border: 'none',
              pointerEvents: 'none',
            }}
          />
        );
      }
      if (viewMode) {
        const text = getString(fieldValues[field.name], '');
        return (
          <div
            title={text}
            style={{ ...baseStyle, display: 'flex', alignItems: 'center', pointerEvents: 'none', mixBlendMode: 'multiply' }}
          >
            <span style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign }}>{text}</span>
          </div>
        );
      }
      return (
        <input
          name={field.name}
          type="date"
          required={field.required}
          value={getString(fieldValues[field.name], '')}
          onChange={e => handleDateChange(field.name, e.target.value)}
          style={{ ...baseStyle }}
        />
      );
    }
    // Handle textarea fields
    if (field.type === 'textarea') {
      if (viewMode) {
        if (isEmptyValue) {
          return (
            <div
              style={{
                ...baseStyle,
                background: 'transparent',
                border: 'none',
                pointerEvents: 'none',
              }}
            />
          );
        }
        const text = getString(fieldValues[field.name], '');
        return (
          <div 
            title={text} 
            style={{ 
              ...baseStyle, 
              pointerEvents: 'none', 
              mixBlendMode: 'multiply',
              whiteSpace: 'pre-wrap',
              overflow: 'hidden',
              wordBreak: 'break-word',
              padding: '4px 8px',
              lineHeight: 1.4
            }}
          >
            {text}
          </div>
        );
      }
      return (
        <textarea
          name={field.name}
          required={field.required}
          value={getString(fieldValues[field.name], '')}
          onChange={handleInputChange}
          style={{ 
            ...baseStyle, 
            resize: 'none',
            padding: '4px 8px',
            lineHeight: 1.4,
            whiteSpace: 'pre-wrap',
            fontFamily: 'inherit'
          }}
          autoComplete="off"
          rows={Math.max(2, Math.floor((y1 - y0) / 20))} // Estimate rows based on height
        />
      );
    }

    if (field.type === 'number') {
      if (viewMode) {
        if (isEmptyValue) {
          return (
            <div
              style={{
                ...baseStyle,
                background: 'transparent',
                border: 'none',
                pointerEvents: 'none',
              }}
            />
          );
        }
        const text = formatNumberDisplay(fieldValues[field.name]);
        return (
          <div title={text} style={{ ...baseStyle, display: 'flex', alignItems: 'center', pointerEvents: 'none', mixBlendMode: 'multiply' }}>
            <span style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign }}>{text}</span>
          </div>
        );
      }
      return (
        <FormattedNumberInput
          name={field.name}
          required={field.required}
          value={getString(fieldValues[field.name], '')}
          onCanonicalChange={(name, canonical) => setFieldValues(prev => ({ ...prev, [name]: canonical }))}
          style={baseStyle}
        />
      );
    }

    // Default: text input, NO placeholder
    if (viewMode) {
      if (isEmptyValue) {
        // In view mode, keep overlays fully transparent
        return (
          <div
            style={{
              ...baseStyle,
              background: 'transparent',
              border: 'none',
              pointerEvents: 'none',
            }}
          />
        );
      }
      const text = getString(fieldValues[field.name], '');
      return (
        <div title={text} style={{ ...baseStyle, display: 'flex', alignItems: 'center', pointerEvents: 'none', mixBlendMode: 'multiply' }}>
          <span style={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign }}>{text}</span>
        </div>
      );
    }
    return (
      <input
        name={field.name}
        type={field.type || 'text'}
        required={field.required}
        value={getString(fieldValues[field.name], '')}
        onChange={handleInputChange}
        style={{ ...baseStyle }}
        autoComplete="off"
      />
    );
  };

  return (
    <div>
      {showPrintButton ? (
        <div className="no-print" style={{ marginBottom: 8 }}>
          <PrintButton
            targetRef={containerRef}
            tooltip="Print form"
            cloneRoot
            extraCss={FORM_PRINT_CSS}
          />
        </div>
      ) : null}
      <div style={{ position: 'relative', display: 'inline-block' }}>
        {colorless ? (
          <div
            className="no-print"
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 1000,
              pointerEvents: 'auto',
              background: 'transparent',
            }}
            aria-hidden
          />
        ) : null}
        <div
          ref={containerRef}
          className="form-print-root"
          style={{
            position: 'relative',
            width: svgWidth * scale,
            height: svgHeight * scale,
            minWidth: 320,
            minHeight: 200,
            background: colorless ? 'transparent' : '#fafcff',
            pointerEvents: 'auto',
            zIndex: 0,
          }}
        >
      {/* SVG background as a separate absolutely positioned layer */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: svgWidth * scale,
          height: svgHeight * scale,
          zIndex: 1,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
        dangerouslySetInnerHTML={{ __html: svg }}
      />
      {/* Overlay fields as a separate absolutely positioned layer above SVG */}
      <div
        className="form-print-fields"
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: svgWidth * scale,
          height: svgHeight * scale,
          pointerEvents: 'auto',
          zIndex: 10,
        }}
      >
        {!(colorless && hideOverlaysInView) ? (
          fields.map((field, idx) => (
            <React.Fragment key={field.name || idx}>{renderOverlayField(field)}</React.Fragment>
          ))
        ) : null}
      </div>
      {/* Modal for custom manual select and all modalComponent fields */}
      {modalVisible && modalField && modalField.modalComponent && (() => {
        // CustomManualModal: use AntD Modal
        if (modalField.modalComponent === 'CustomManualModal') {
          return (
            <Modal
              open={modalVisible}
              title={null}
              onCancel={handleModalCancel}
              footer={[
                <Button key="cancel" onClick={handleModalCancel}>Cancel</Button>,
                <Button key="ok" type="primary" onClick={() => handleModalOk(selectedOption)} disabled={false}>Select</Button>,
              ]}
              destroyOnClose
              centered
              width={360}
              closable={false}
            >
              <Select
                allowClear
                showSearch
                style={{ width: '100%', borderRadius: 0, background: '#e6f2ff', border: 'none' }}
                placeholder="Select option"
                value={selectedOption}
                onChange={setSelectedOption}
                optionFilterProp="children"
                dropdownStyle={{ borderRadius: 0 }}
                filterOption={(input, option) =>
                  String(option?.children).toLowerCase().includes(input.toLowerCase())
                }
              >
                {(Array.isArray(modalField.options) ? modalField.options : []).map((opt: any, idx: number) => (
                  <Select.Option key={idx} value={opt}>
                    {opt}
                  </Select.Option>
                ))}
              </Select>
            </Modal>
          );
        }
        // All other modalComponent: render ONLY the modal from modalRegistry, not inside AntD Modal
        const modalEntry = modalRegistry.find(m => m.name === modalField.modalComponent);
        const ModalComp = modalEntry ? modalEntry.component : null;
        if (!ModalComp) return null;
        if (modalField.modalComponent === 'SelectDCCFileModal') {
          return (
            <AnimatePresence>
              {modalVisible && (
                <SelectDCCFileModal
                  open={modalVisible}
                  value={fieldValues[modalField.name]}
                  mode="multiple"
                  onOk={handleModalOk}
                  onCancel={handleModalCancel}
                  options={modalField.options}
                />
              )}
            </AnimatePresence>
          );
        }
        return (
          <AnimatePresence>
            {modalVisible && (
              <ModalComp
                open={modalVisible}
                value={fieldValues[modalField.name]}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                options={modalField.options}
              />
            )}
          </AnimatePresence>
        );
      })()}
      <Spin spinning={loading} />
    </div>
      </div>
    </div>
  );
};

export default GenericFormFill;
